import { useEffect, useMemo, useRef, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import debounce from 'lodash.debounce';

import Button from '@/components/ui/button';
import PageShell from '@/components/PageShell';
import PageCTA from '@/components/PageCTA';
import MemoryBoxCard from '@/components/MemoryBoxCard';
import MessageTabs from '@/components/message/MessageTabs';
import {
  MESSAGE_TABS,
  MESSAGE_TAB_LABEL,
  messagePanelId,
  messageTabId,
  type MessageTab,
} from '@/components/message/tabs';
import TextComposer from '@/components/message/TextComposer';
import PhotoAttachment from '@/components/message/PhotoAttachment';
import ConfirmMessageSheet from '@/components/message/ConfirmMessageSheet';
import RecordingIdeasDialog from '@/components/message/RecordingIdeasDialog';
import VideoRecorder, {
  type VideoRecorderHandle,
} from '@/components/media-recorders/VideoRecorder';
import VoiceRecorder, {
  type VoiceRecorderHandle,
} from '@/components/media-recorders/VoiceRecorder';
import {
  Copyright as LandingCopyright,
  GiftSummaryChip,
  PartnerBar,
} from '@/components/LandingContext';

import useIsNavigating from '@/hooks/useIsNavigating';
import usePlatform from '@/hooks/usePlatform';
import useIsOnline from '@/hooks/useIsOnline';
import {
  useAudioUploadMutation,
  useGiftTextMessageMutation,
  usePhotoUploadMutation,
  useVideoUploadMutation,
} from '@/tsq/mutations';
import {
  useAmount,
  useChildFirstName,
  useContributionId,
  useGiftImageUrl,
  useGiftInviteCode,
  useHydrated,
  useMessageData,
  useMessageDurationSec,
  useMessageSaved,
  useSetMessageData,
  useSetMessageDurationSec,
  useSetMessageSaved,
  useSetMessageType,
  useShowChildPhoto,
} from '@/store';
import {
  useClearEphemeralAudio,
  useClearEphemeralVideo,
  useEphemeralAudio,
  useEphemeralVideo,
  useSetEphemeralAudio,
  useSetEphemeralVideo,
} from '@/store/ephemeralStore';
import {
  DEBOUNCE_DELAY_MS,
  MESSAGE_MAX_LENGTH,
  PHOTO_MAX_BYTES,
  PHOTO_MIME_TYPES,
  storageKeys,
} from '@/constants';
import { formatCurrency } from '@/utils/formatters/currency';
import { canRecordMedia, validatePhotoFile } from '@/utils/helpers/media.helpers';
import { track } from '@/analytics';
import { normalizeAndSanitizeString } from '@/utils/formatters/string';
import { blobStorage } from '@/utils/helpers/storage.helpers';

/** How an already-created message is referred to in the replace warning. */
const TYPE_NOUN: Record<MessageTab, string> = {
  text: 'written message',
  voice: 'voice message',
  video: 'video message',
};

const TYPE_ACTION: Record<MessageTab, string> = {
  text: 'write a message',
  voice: 'record a voice message',
  video: 'record a video',
};

const SAVE_ERROR: Record<MessageTab, string> = {
  text: "We couldn't save your message. Please try again.",
  voice: "We couldn't upload your recording. Please try again.",
  video: "We couldn't upload your recording. Please try again.",
};

const PHOTO_UPLOAD_ERROR = "We couldn't upload your photo. Please try again.";
const PHOTO_TYPE_ERROR = 'Please choose a JPG or PNG image.';
const PHOTO_SIZE_ERROR = `That photo is too large. Please choose one under ${Math.round(
  PHOTO_MAX_BYTES / (1024 * 1024),
)}MB.`;

const DUPLICATE_ERROR = "You've already added a message to this gift.";

const TEXT_ONLY: readonly MessageTab[] = ['text'];

/** What the pending confirmation is about — a tab switch, or Continue. */
type PendingConfirm =
  | { kind: 'switch'; target: MessageTab; existing: MessageTab }
  | { kind: 'skip'; existing: MessageTab };

function AddMessage() {
  const navigate = useNavigate();
  const isOnline = useIsOnline();
  const isNavigating = useIsNavigating();
  const platform = usePlatform();

  // ── Store hooks — ALL before any early return ─────────────────────────────

  const hasHydrated = useHydrated();
  const giftInviteCode = useGiftInviteCode();
  const contributionId = useContributionId();
  const amount = useAmount();
  const childFirstName = useChildFirstName();
  const photoSignedUrl = useGiftImageUrl();
  const showChildPhoto = useShowChildPhoto();

  const savedMessage = useMessageData();
  const messageSaved = useMessageSaved();
  const messageDurationSec = useMessageDurationSec();

  const setMessageType = useSetMessageType();
  const setMessageData = useSetMessageData();
  const setMessageDurationSec = useSetMessageDurationSec();
  const setMessageSaved = useSetMessageSaved();

  const ephemeralAudio = useEphemeralAudio();
  const ephemeralVideo = useEphemeralVideo();
  const setEphemeralAudio = useSetEphemeralAudio();
  const setEphemeralVideo = useSetEphemeralVideo();
  const clearEphemeralAudio = useClearEphemeralAudio();
  const clearEphemeralVideo = useClearEphemeralVideo();

  // ── Mutations — before any early return ───────────────────────────────────

  const { mutateAsync: saveTextMessage } = useGiftTextMessageMutation();
  const { mutateAsync: uploadPhoto } = usePhotoUploadMutation();
  const { mutateAsync: uploadAudio } = useAudioUploadMutation();
  const { mutateAsync: uploadVideo } = useVideoUploadMutation();

  // ── Local state — before any early return ─────────────────────────────────

  // AC 1: Text is selected on load.
  const [activeTab, setActiveTab] = useState<MessageTab>('text');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<{ file: Blob; url: string } | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [hasVoice, setHasVoice] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(
    null,
  );
  const [suppressSwitchWarning, setSuppressSwitchWarning] = useState(false);
  // AC 9: a browser with no MediaRecorder gets the Text tab only. Resolved
  // once on mount — the capability cannot change mid-session.
  const [availableTabs] = useState<readonly MessageTab[]>(() =>
    canRecordMedia() ? MESSAGE_TABS : TEXT_ONLY,
  );

  const videoRecorderRef = useRef<VideoRecorderHandle>(null);
  const voiceRecorderRef = useRef<VoiceRecorderHandle>(null);

  // Snapshot of messageSaved at mount. The redirect below keys off this rather
  // than the live value, so it only fires when the user arrives with a message
  // already saved — never during this session's own submit, where
  // setMessageSaved(true) runs immediately before navigate().
  const messageSavedOnMount = useRef(messageSaved);

  // ── Draft persistence ─────────────────────────────────────────────────────

  const debouncedSetMessageData = useMemo(
    () => debounce((val: string) => setMessageData(val), DEBOUNCE_DELAY_MS),
    [setMessageData],
  );

  useEffect(() => () => debouncedSetMessageData.cancel(), [
    debouncedSetMessageData,
  ]);

  // Restore a typed draft once the persisted store has rehydrated. Depends on
  // hasHydrated alone so it runs exactly once (false → true) and can't fight
  // the user's typing.
  useEffect(() => {
    if (!hasHydrated) return;
    if (savedMessage) setText(savedMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated]);

  // ── Refresh recovery ──────────────────────────────────────────────────────
  //
  // A hard reload wipes the JS heap: the ephemeral store is empty even if the
  // user recorded something and has not submitted yet. Restore from IndexedDB.
  useEffect(() => {
    if (messageSaved) return;
    let cancelled = false;

    (async () => {
      try {
        const [audioBlob, videoBlob, photoBlob] = await Promise.all([
          blobStorage.get(storageKeys.audioBlob as keyof typeof storageKeys),
          blobStorage.get(storageKeys.videoBlob as keyof typeof storageKeys),
          blobStorage.get(
            storageKeys.messagePhotoBlob as keyof typeof storageKeys,
          ),
        ]);

        if (cancelled) return;

        // The photo belongs to the Text tab, which is already the default —
        // restoring it must not pull focus away from a recovered recording.
        if (photoBlob) {
          setPhoto({ file: photoBlob, url: URL.createObjectURL(photoBlob) });
        }

        if (audioBlob) {
          if (!ephemeralAudio) setEphemeralAudio(audioBlob);
          setHasVoice(true);
          setActiveTab('voice');
        }
        if (videoBlob) {
          if (!ephemeralVideo) setEphemeralVideo(videoBlob);
          setHasVideo(true);
          setActiveTab('video');
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            'Refresh recovery: failed to read blobs from IndexedDB',
            err,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The page owns this object URL (the ephemeral store only handles the
  // recordings), so it has to revoke it when the page goes away.
  const photoUrlRef = useRef<string | null>(null);
  photoUrlRef.current = photo?.url ?? null;

  useEffect(
    () => () => {
      if (photoUrlRef.current) URL.revokeObjectURL(photoUrlRef.current);
    },
    [],
  );

  // ── Guards ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (giftInviteCode && contributionId && !messageSavedOnMount.current) {
      return;
    }

    if (!giftInviteCode) {
      console.error('Missing giftInviteCode in AddMessage component');
      // About Us renders this, so it replaces the toast rather than needing one.
      navigate('/about-us', {
        replace: true,
        state: {
          message:
            "We couldn't find your gift link. Please open the link you were sent and we'll pick up from there.",
        },
      });
      return;
    }

    if (!contributionId) {
      console.error('Missing contributionId in AddMessage component');
      // Straight back to the amount step, which explains itself.
      navigate(
        {
          pathname: '/',
          search: `?${createSearchParams({ code: giftInviteCode })}`,
        },
        { replace: true },
      );
      return;
    }

    // A message is already committed for this contribution, so this step has
    // nothing left to do — skip forward silently. It used to raise a toast,
    // which the V2 design has no place for (GIFTWEB-26).
    if (messageSavedOnMount.current && !isNavigating) {
      navigate('/payment', { replace: true });
    }
  }, [giftInviteCode, contributionId, isNavigating, navigate]);

  // ── Derived — before any early return ─────────────────────────────────────

  const hasText = text.trim().length > 0;

  // AC 7: a photo on its own is a message, with or without any typed text.
  const hasContent = (tab: MessageTab) =>
    tab === 'text' ? hasText || photo !== null : tab === 'voice' ? hasVoice : hasVideo;

  /** What the replace warning calls what is already on a tab. */
  const nounFor = (tab: MessageTab) => {
    if (tab !== 'text') return TYPE_NOUN[tab];
    if (hasText && photo) return 'message and photo';
    return hasText ? TYPE_NOUN.text : 'photo';
  };

  const tabsWithContent = availableTabs.filter(hasContent);
  const otherContent = tabsWithContent.filter((t) => t !== activeTab);

  // ── Early returns — after ALL hooks ───────────────────────────────────────

  if (!contributionId || !giftInviteCode) return null;
  if (messageSavedOnMount.current) return null;

  // ── Tab switching ─────────────────────────────────────────────────────────

  const requestTab = (tab: MessageTab) => {
    if (tab === activeTab || isRecording) return;
    setFormError(null);

    // AC 3: only one message is sent, so warn before a second one is started.
    const conflicting = tabsWithContent.filter((t) => t !== tab);
    if (conflicting.length === 0 || suppressSwitchWarning) {
      setActiveTab(tab);
      return;
    }

    setPendingConfirm({ kind: 'switch', target: tab, existing: conflicting[0] });
  };

  // ── Recording handlers ────────────────────────────────────────────────────

  const handleVoiceComplete = async (blob: Blob, _url: string, duration: number) => {
    setIsRecording(false);
    setHasVoice(true);
    setEphemeralAudio(blob);
    setMessageDurationSec(duration || 0);

    try {
      await blobStorage.set(storageKeys.audioBlob, blob);
    } catch (err) {
      // The recording still lives in memory and sends fine this session; only
      // refresh recovery is lost. We accept that — a reload will simply have no
      // recording to restore, prompting the user to re-record.
      console.error('Error saving audio blob to storage:', err);
    }
  };

  const handleVoiceDelete = async () => {
    setIsRecording(false);
    setHasVoice(false);
    clearEphemeralAudio();
    setMessageDurationSec(0);

    try {
      await blobStorage.remove(storageKeys.audioBlob);
    } catch (err) {
      console.error('Error removing audio blob from storage:', err);
    }
  };

  const handleVideoComplete = async (blob: Blob, _url: string, duration: number) => {
    setIsRecording(false);
    setHasVideo(true);

    // Release the camera so Safari can move the audio session out of
    // PlayAndRecord before the clip is played back — without this the
    // playback audio is decoded against the wrong session state.
    videoRecorderRef.current?.releaseStream();

    setEphemeralVideo(blob);
    setMessageDurationSec(duration || 0);

    try {
      await blobStorage.set(storageKeys.videoBlob, blob);
    } catch (err) {
      // The recording still lives in memory and sends fine this session; only
      // refresh recovery is lost. We accept that — a reload will simply have no
      // recording to restore, prompting the user to re-record.
      console.error('Error saving video blob to storage:', err);
    }
  };

  const handleVideoDelete = async () => {
    setIsRecording(false);
    setHasVideo(false);
    clearEphemeralVideo();
    setMessageDurationSec(0);

    try {
      await blobStorage.remove(storageKeys.videoBlob);
    } catch (err) {
      console.error('Error removing video blob from storage:', err);
    }
  };

  // ── Photo (GIFTWEB-22) ────────────────────────────────────────────────────

  const attachPhoto = async (file: File) => {
    setFormError(null);

    const rejection = validatePhotoFile(file, {
      maxBytes: PHOTO_MAX_BYTES,
      mimeTypes: PHOTO_MIME_TYPES,
    });

    // AC 4/5: nothing is attached when the file is rejected.
    if (rejection) {
      setPhotoError(
        rejection === 'size' ? PHOTO_SIZE_ERROR : PHOTO_TYPE_ERROR,
      );
      return;
    }

    setPhotoError(null);
    if (photo) URL.revokeObjectURL(photo.url);
    setPhoto({ file, url: URL.createObjectURL(file) });

    try {
      await blobStorage.set(storageKeys.messagePhotoBlob, file);
    } catch (err) {
      console.error('Error saving message photo to storage:', err);
    }
  };

  const removePhoto = async () => {
    if (photo) URL.revokeObjectURL(photo.url);
    setPhoto(null);
    setPhotoError(null);

    try {
      await blobStorage.remove(storageKeys.messagePhotoBlob);
    } catch (err) {
      console.error('Error removing message photo from storage:', err);
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const goToPayment = (replace: boolean) => navigate('/payment', { replace });

  const submitMessage = async () => {
    setFormError(null);
    setIsSubmitting(true);

    try {
      if (activeTab === 'text') {
        const safeText = normalizeAndSanitizeString(text);
        if (photo) {
          // One media message carries both, so nothing is committed until the
          // photo itself lands (AC 8).
          await uploadPhoto({
            blob: photo.file,
            text: safeText || undefined,
            uuid: contributionId,
            code: giftInviteCode,
          });
        } else {
          await saveTextMessage({
            text: safeText,
            uuid: contributionId,
            code: giftInviteCode,
          });
        }
        setMessageData(safeText);
        setMessageType('text');
      } else if (activeTab === 'voice') {
        if (!ephemeralAudio) throw new Error('Missing audio recording');
        await uploadAudio({
          blob: ephemeralAudio.blob,
          duration: messageDurationSec || 0,
          uuid: contributionId,
          code: giftInviteCode,
        });
        setMessageType('audio');
      } else {
        if (!ephemeralVideo) throw new Error('Missing video recording');
        await uploadVideo({
          blob: ephemeralVideo.blob,
          duration: messageDurationSec || 0,
          uuid: contributionId,
          code: giftInviteCode,
        });
        setMessageType('video');
      }

      setMessageSaved(true);
      // The kind of message, never its contents.
      track('messageSubmitted', {
        kind:
          activeTab === 'text'
            ? photo
              ? 'photo'
              : 'text'
            : activeTab === 'voice'
              ? 'audio'
              : 'video',
      });
      goToPayment(true);
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      // GIFTWEB-25 AC 4: a 409 means this contribution already carries a
      // message. GIFTWEB-22 AC 9: a photo failure names the photo, and the
      // selection is kept either way.
      const failure =
        activeTab === 'text' && photo ? PHOTO_UPLOAD_ERROR : SAVE_ERROR[activeTab];
      setFormError(status === 409 ? DUPLICATE_ERROR : failure);
      console.error('Error saving gift message:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (isSubmitting) return;

    if (!hasContent(activeTab)) {
      // The active tab is empty but another one holds a message that would be
      // silently dropped — make that explicit before moving on.
      if (otherContent.length > 0) {
        setPendingConfirm({ kind: 'skip', existing: otherContent[0] });
        return;
      }
      // AC 8: continuing with no message at all is allowed.
      track('messageSkipped');
      goToPayment(false);
      return;
    }

    void submitMessage();
  };

  const confirmPending = () => {
    if (!pendingConfirm) return;
    const pending = pendingConfirm;
    setPendingConfirm(null);

    if (pending.kind === 'switch') {
      setActiveTab(pending.target);
    } else {
      track('messageSkipped');
      goToPayment(false);
    }
  };

  // ── Confirmation copy ─────────────────────────────────────────────────────

  const confirmCopy = pendingConfirm
    ? pendingConfirm.kind === 'switch'
      ? {
          title: `Replace your ${nounFor(pendingConfirm.existing)}?`,
          description: `Only one message is sent with your gift. If you ${TYPE_ACTION[pendingConfirm.target]}, your ${nounFor(pendingConfirm.existing)} won't be sent.`,
          confirmLabel: `Switch to ${MESSAGE_TAB_LABEL[pendingConfirm.target]}`,
          showSuppress: true,
        }
      : {
          title: `Continue without your ${nounFor(pendingConfirm.existing)}?`,
          description: `Only the ${MESSAGE_TAB_LABEL[activeTab]} tab is sent with your gift and it is empty, so your ${nounFor(pendingConfirm.existing)} won't be sent.`,
          confirmLabel: 'Continue anyway',
          showSuppress: false,
        }
    : null;

  // ── Render ────────────────────────────────────────────────────────────────

  const child = childFirstName?.trim() || '';
  const childPossessive = child ? `${child}'s` : 'their';
  const amountLabel = formatCurrency({ amount, precision: 0 });

  return (
    <>
      <PageShell
        variant='split'
        className='add-message-page'
        left={
          <>
            <GiftSummaryChip
              firstName={childFirstName}
              photoUrl={photoSignedUrl}
              showPhoto={showChildPhoto}
              amountLabel={amountLabel}
            />

            <div className='text-left'>
              <h1 className='text-[27px] leading-tight font-bold tracking-[-0.02em] text-[#1a201d] lg:text-[31px] lg:tracking-[-0.025em]'>
                Add a message?
              </h1>
              <p className='mt-2.5 text-[14px] leading-[1.55] text-[#5a635e] lg:mt-[11px] lg:text-[14.5px]'>
                Add a personal message, opened by {childPossessive} parent today
                – they could save it for {child || 'them'} to enjoy again when
                they turn 18.
              </p>
            </div>

            <div className='hidden lg:block'>
              <MemoryBoxCard childFirstName={childFirstName} />
              <PartnerBar variant='desktop' />
              <LandingCopyright marginTop={12} />
            </div>
          </>
        }
        right={
          <>
            <div className='text-left'>
              <p className='mx-0.5 mb-[11px] text-[14px] font-bold text-[#1a201d] lg:mb-3 lg:text-[15px]'>
                Add a message
              </p>

              <MessageTabs
                tabs={availableTabs}
                selected={activeTab}
                onSelect={requestTab}
                disabled={isRecording}
              />

              <div
                role='tabpanel'
                id={messagePanelId(activeTab)}
                aria-labelledby={messageTabId(activeTab)}
                className='mt-4'
              >
                {activeTab === 'text' && (
                  <TextComposer
                    value={text}
                    onChange={(value) => {
                      setText(value);
                      debouncedSetMessageData(value);
                    }}
                    maxLength={MESSAGE_MAX_LENGTH}
                    childFirstName={childFirstName}
                  >
                    <PhotoAttachment
                      previewUrl={photo?.url ?? null}
                      onSelect={(file) => void attachPhoto(file)}
                      onRemove={() => void removePhoto()}
                      error={photoError}
                      childFirstName={childFirstName}
                      disabled={isSubmitting}
                      platform={platform}
                    />
                  </TextComposer>
                )}

                {activeTab === 'voice' && (
                  <VoiceRecorder
                    ref={voiceRecorderRef}
                    onRecordingStart={() => setIsRecording(true)}
                    onRecordingComplete={handleVoiceComplete}
                    onRecordingDelete={handleVoiceDelete}
                    existingVoiceUrl={ephemeralAudio?.url || ''}
                    existingVoiceBlob={ephemeralAudio?.blob ?? null}
                    existingVoiceDuration={messageDurationSec || 0}
                    idleFooter={
                      <RecordingIdeasDialog childFirstName={childFirstName} />
                    }
                    isActive
                  />
                )}

                {activeTab === 'video' && (
                  <VideoRecorder
                    ref={videoRecorderRef}
                    onRecordingStart={() => setIsRecording(true)}
                    onRecordingComplete={handleVideoComplete}
                    onRecordingDelete={handleVideoDelete}
                    existingVideoUrl={ephemeralVideo?.url || ''}
                    existingVideoDuration={messageDurationSec || 0}
                    idleFooter={
                      <RecordingIdeasDialog childFirstName={childFirstName} />
                    }
                    isActive
                  />
                )}
              </div>
            </div>

            <MemoryBoxCard childFirstName={childFirstName} className='lg:hidden' />

            {formError && (
              <p
                role='alert'
                data-testid='message-form-error'
                className='rounded-[13px] border border-[#fecdd3] bg-[#fff1f2] px-3.5 py-3 text-left text-[12.5px] leading-[1.45] text-[#9f1239]'
              >
                {formError}
              </p>
            )}

            <PageCTA>
              <Button
                type='button'
                className='w-full'
                onClick={handleContinue}
                disabled={!isOnline || isSubmitting || isRecording}
                data-testid='message-continue'
              >
                <span>
                  {isSubmitting ? 'Saving…' : 'Continue to payment'}
                </span>
                {!isSubmitting && (
                  <svg
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2.2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                    className='size-4.5!'
                  >
                    <path d='M5 12h14M13 6l6 6-6 6' />
                  </svg>
                )}
              </Button>
              <div className='lg:hidden'>
                <LandingCopyright marginTop={0} />
              </div>
            </PageCTA>
          </>
        }
      />

      {confirmCopy && (
        <ConfirmMessageSheet
          open={pendingConfirm !== null}
          onOpenChange={(open) => {
            if (!open) setPendingConfirm(null);
          }}
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          onConfirm={confirmPending}
          showSuppress={confirmCopy.showSuppress}
          suppressed={suppressSwitchWarning}
          onSuppressChange={setSuppressSwitchWarning}
        />
      )}
    </>
  );
}

export default AddMessage;
